<template>
  <div class="dashboard-page">
    <div
      v-if="mobileMenuOpen"
      class="mobile-dashboard-backdrop"
      aria-hidden="true"
      @click="closeMobileMenu"
    ></div>

    <aside :class="['dashboard-sidebar', { 'mobile-menu-open': mobileMenuOpen }]">
      <a class="dashboard-brand" href="/" @click="closeMobileMenu">
        <span class="dashboard-logo" aria-hidden="true">
          <span></span>
        </span>
        <span>
          <strong>Codex API</strong>
          <em>中转服务</em>
        </span>
      </a>

      <nav class="dashboard-nav" aria-label="dashboard navigation">
        <router-link
          v-for="item in visibleNavItems"
          :key="item.name"
          :to="{ name: item.name }"
          custom
          v-slot="{ navigate, isExactActive }"
        >
          <button
            :class="{ active: isExactActive }"
            type="button"
            @click="navigate($event); closeMobileMenu()"
          >
            <i :class="item.icon"></i>
            <span>{{ item.label }}</span>
          </button>
        </router-link>
      </nav>
    </aside>

    <main class="dashboard-main">
      <header class="dashboard-topbar">
        <div class="dashboard-heading">
          <button
            class="mobile-dashboard-toggle"
            type="button"
            :aria-expanded="String(mobileMenuOpen)"
            aria-label="打开菜单"
            @click="toggleMobileMenu"
          >
            <i class="el-icon-menu"></i>
          </button>
          <div>
            <h1>{{ currentTitle }}</h1>
            <p>{{ currentSubtitle }}</p>
          </div>
        </div>
        <div class="topbar-actions">
          <button class="balance-promo" type="button" title="查看余额分组" @click="$router.push({ name: 'dashboardSubscription' })">
            <i class="el-icon-wallet" aria-hidden="true"></i>
            <span>余额 {{ balanceGroupAmount === null ? '--' : '$' + formatBalanceAmount(balanceGroupAmount) }}</span>
          </button>
          <button v-if="timeoutCompensationEnabled" class="checkin-promo timeout-compensation-promo" type="button" :disabled="timeoutCompensationLoading" @click="openTimeoutCompensation">
            <i class="el-icon-time"></i>
            <span>超时赔付</span>
          </button>
          <button v-if="checkinAvailabilityLoaded && checkinVisible" class="checkin-promo" type="button" :disabled="checkinAvailabilityLoading" @click="openCheckinPage">
            <i class="el-icon-present"></i>
            <span>{{ checkinCheckedToday ? '今日已领取' : checkinQuotaRemaining === 0 ? '可领额度已领完' : '每日赠额度' }}</span>
          </button>
          <button class="feedback-promo" type="button" @click="openFeedback">
            <i class="el-icon-chat-dot-round"></i>
            <span>提工单</span>
            <i v-if="unreadFeedbackCount > 0" class="feedback-unread-dot" aria-label="有新的反馈回复"></i>
          </button>
          <a
            class="tutorial-promo"
            href="https://docs.qq.com/doc/DRmNJTmJ1TUFUWWF2"
            target="_blank"
            rel="noopener noreferrer"
            @click="recordTutorialAccess"
          >
            <i class="el-icon-document" aria-hidden="true"></i>
            <span>查看使用教程</span>
          </a>
          <el-tag v-if="customerService" class="customer-service" type="warning" effect="plain">
            <i class="el-icon-service"></i>
            <span>{{ customerService }}</span>
          </el-tag>
          <el-dropdown trigger="click" @command="handleUserCommand">
            <button class="user-pill" type="button">
              <i class="el-icon-user-solid"></i>
              <span>{{ userName }}</span>
              <i class="el-icon-arrow-down"></i>
            </button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <router-view></router-view>
      <AnnouncementDialog
        v-if="latestAnnouncement"
        :announcement="latestAnnouncement"
        :visible.sync="announcementDialogVisible"
      />
    </main>

    <el-dialog
      title="超时赔付服务说明"
      :visible.sync="timeoutCompensationDialogVisible"
      width="520px"
      custom-class="timeout-compensation-dialog"
      :close-on-click-modal="false"
    >
      <div v-if="timeoutCompensation" class="timeout-compensation-content">
        <p class="timeout-compensation-intro">
          我们始终把响应速度和使用体验放在首位，并持续优化服务链路，尽力让每一次请求都能更快得到响应。
        </p>
        <p class="timeout-compensation-intro">
          但受官方渠道响应限制及网络波动影响，部分请求仍可能出现首字响应较慢的情况。由此带来的等待，我们深表歉意。
        </p>
        <p class="timeout-compensation-intro">
          为了改善您的使用体验，也为了表达我们的诚意，昨日首字响应超过 30 秒的请求，我们将按实际消耗额度的三分之一进行补偿，并直接发放到您的余额分组。
        </p>
        <div class="timeout-compensation-stats">
          <div><span>统计日期</span><strong>{{ timeoutCompensation.usageDate }}</strong></div>
          <div><span>超时请求</span><strong>{{ timeoutCompensation.requestCount || 0 }} 次</strong></div>
          <div><span>涉及费用</span><strong>{{ formatCompensationAmount(timeoutCompensation.sourceAmount) }}</strong></div>
          <div><span>补偿额度</span><strong class="timeout-compensation-amount">{{ formatCompensationAmount(timeoutCompensation.compensationAmount) }}</strong></div>
        </div>
        <p v-if="timeoutCompensation.claimed" class="timeout-compensation-result is-claimed">
          昨日的超时赔付额度已经发放，无需重复领取。
        </p>
        <p v-else-if="!hasTimeoutCompensation" class="timeout-compensation-result">
          昨日没有符合超时赔付条件的请求。
        </p>
        <p v-else class="timeout-compensation-result">
          每个用户同一个统计日期只能领取一次，确认后额度会直接到账。
        </p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="timeoutCompensationDialogVisible = false">暂不领取</el-button>
        <el-button
          v-if="timeoutCompensation && !timeoutCompensation.claimed && hasTimeoutCompensation"
          type="primary"
          :loading="timeoutCompensationClaiming"
          @click="claimTimeoutCompensationNow"
        >确认领取 {{ formatCompensationAmount(timeoutCompensation.compensationAmount) }}</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import { fetchProfile } from '../api/profile';
import { fetchSubscriptionDashboard } from '../api/subscription';
import { fetchCheckinAvailability } from '../api/checkin';
import { fetchUnreadFeedbackCount } from '../api/feedback';
import { recordAccess } from '../api/accessRecord';
import { claimTimeoutCompensation, fetchTimeoutCompensationPreview } from '../api/usage';
import { fetchLatestAnnouncement, markAnnouncementRead } from '../api/announcements';
import AnnouncementDialog from '../components/AnnouncementDialog.vue';

const LOGIN_STORAGE_KEY = 'codex-api-login-user';
const TOKEN_STORAGE_KEY = 'codex-api-auth-token';
const TUTORIAL_URL = 'https://docs.qq.com/doc/DRmNJTmJ1TUFUWWF2';
const CHECKIN_ACCESS_URL = '/home/checkin-button';

export default {
  name: 'DashboardView',
  components: { AnnouncementDialog },
  data() {
    return {
      userName: window.localStorage.getItem(LOGIN_STORAGE_KEY) || '未登录用户',
      mobileMenuOpen: false,
      balanceGroupAmount: null,
      customerService: '',
      proxyEnabled: false,
      checkinAllowed: false,
      checkinVisible: false,
      checkinAvailabilityLoaded: false,
      checkinCheckedToday: false,
      checkinQuotaRemaining: null,
      checkinAvailabilityLoading: false,
      checkinAvailabilityReloadRequested: false,
      timeoutCompensationEnabled: false,
      timeoutCompensationDialogVisible: false,
      timeoutCompensationLoading: false,
      timeoutCompensationClaiming: false,
      timeoutCompensation: null,
      latestAnnouncement: null,
      announcementDialogVisible: false,
      unreadFeedbackCount: 0,
      navItems: [
        { name: 'dashboardOverview', label: '仪表盘', subtitle: '欢迎回来，这是您的账户概览。', icon: 'el-icon-data-analysis' },
        { name: 'dashboardKeys', label: 'API 密钥', subtitle: '创建、查看和管理您的访问密钥。', icon: 'el-icon-key' },
        { name: 'dashboardModels', label: '可用模型', subtitle: '查看当前公开支持的模型及 Token 计费价格。', icon: 'el-icon-data-analysis' },
        { name: 'dashboardSubscription', label: '我的套餐', subtitle: '查看当前套餐、用量进度和升级方案。', icon: 'el-icon-medal' },
        { name: 'dashboardBilling', label: '充值', subtitle: '', icon: 'el-icon-wallet' },
        { name: 'dashboardRedeem', label: '兑换码使用', subtitle: '使用兑换码领取余额或日卡套餐。', icon: 'el-icon-present' },
        { name: 'dashboardCheckin', label: '每日赠额度', subtitle: '平台每日赠送使用额度，领取后即可使用。', icon: 'el-icon-date', requiresCheckin: true },
        { name: 'dashboardUsage', label: '使用记录', subtitle: '查看请求明细、Token 消耗和调用状态。', icon: 'el-icon-tickets' },
        { name: 'dashboardQuotaRecords', label: '额度记录', subtitle: '', icon: 'el-icon-notebook-2' },
        { name: 'dashboardAnnouncements', label: '公告', subtitle: '查看系统公告和阅读状态', icon: 'el-icon-bell' },
        { name: 'dashboardFeedback', label: '我的工单', subtitle: '提交建议或问题，查看处理状态和管理员回复。', icon: 'el-icon-chat-line-round' },
        { name: 'dashboardProxyGuide', label: '我要开通代理', subtitle: '了解如何快速拥有自己的代理服务站点。', icon: 'el-icon-connection', requiresProxy: true },
        { name: 'dashboardProfile', label: '个人资料', subtitle: '维护账号资料和通知偏好。', icon: 'el-icon-user' }
      ]
    };
  },
  created() {
    this.loadBalanceGroupAmount();
    this.loadCustomerService();
    this.loadUnreadFeedbackCount();
    this.loadLatestAnnouncement();
    this.loadCheckinAvailability();
  },
  mounted() {
    this.$root.$on('feedback-read', this.handleFeedbackReadCompleted);
    this.$root.$on('checkin-availability-updated', this.applyCheckinAvailability);
    this.$root.$on('checkin-completed', this.loadBalanceGroupAmount);
    this.$root.$on('redeem-completed', this.loadCheckinAvailability);
    window.addEventListener('focus', this.loadCheckinAvailability);
  },
  beforeDestroy() {
    this.checkinAvailabilityReloadRequested = false;
    this.$root.$off('feedback-read', this.handleFeedbackReadCompleted);
    this.$root.$off('checkin-availability-updated', this.applyCheckinAvailability);
    this.$root.$off('checkin-completed', this.loadBalanceGroupAmount);
    this.$root.$off('redeem-completed', this.loadCheckinAvailability);
    window.removeEventListener('focus', this.loadCheckinAvailability);
  },
  computed: {
    hasTimeoutCompensation() {
      return this.timeoutCompensation
        && Number(this.timeoutCompensation.compensationAmount || 0) > 0
        && Number(this.timeoutCompensation.requestCount || 0) > 0;
    },
    currentNav() {
      return this.visibleNavItems.find(item => item.name === this.$route.name) || this.navItems[0];
    },
    visibleNavItems() {
      return this.navItems.filter(item => (!item.requiresCheckin || this.checkinVisible)
        && (!item.requiresProxy || this.proxyEnabled));
    },
    currentTitle() {
      return this.$route.meta.title || this.currentNav.label;
    },
    currentSubtitle() {
      return this.$route.meta.subtitle || this.currentNav.subtitle;
    }
  },
  methods: {
    async loadBalanceGroupAmount() {
      try {
        const { data } = await fetchSubscriptionDashboard();
        if (data && data.code === 0 && data.data) {
          this.balanceGroupAmount = Number(data.data.balanceGroupRemainingAmount || 0);
        }
      } catch (error) {
        // Keep the last known amount when the balance endpoint is unavailable.
      }
    },
    formatBalanceAmount(value) {
      return Number(value || 0).toFixed(2);
    },
    async openTimeoutCompensation() {
      if (this.timeoutCompensationLoading || !this.timeoutCompensationEnabled) {
        return;
      }
      this.timeoutCompensationLoading = true;
      try {
        const { data } = await fetchTimeoutCompensationPreview();
        if (!data || data.code !== 0 || !data.data) {
          throw new Error((data && data.message) || '超时赔付统计失败');
        }
        this.timeoutCompensation = data.data;
        this.timeoutCompensationDialogVisible = true;
      } catch (error) {
        this.$message.error(error.message || '超时赔付统计失败');
      } finally {
        this.timeoutCompensationLoading = false;
      }
    },
    async claimTimeoutCompensationNow() {
      if (this.timeoutCompensationClaiming || !this.hasTimeoutCompensation) {
        return;
      }
      this.timeoutCompensationClaiming = true;
      try {
        const { data } = await claimTimeoutCompensation();
        if (!data || data.code !== 0 || !data.data) {
          throw new Error((data && data.message) || '超时赔付发放失败');
        }
        this.timeoutCompensation = data.data;
        this.$message.success('超时赔付额度已发放到余额分组');
      } catch (error) {
        this.$message.error(error.message || '超时赔付发放失败');
      } finally {
        this.timeoutCompensationClaiming = false;
      }
    },
    formatCompensationAmount(value) {
      return '$' + Number(value || 0).toFixed(2);
    },
    async loadUnreadFeedbackCount() {
      try {
        const { data } = await fetchUnreadFeedbackCount();
        if (data && data.code === 0) this.unreadFeedbackCount = Math.max(0, Number(data.data) || 0);
      } catch (error) {
        // Feedback reminders are non-critical; retain the last known count.
      }
    },
    async loadLatestAnnouncement() {
      try {
        const { data } = await fetchLatestAnnouncement();
        const announcement = data && data.code === 0 ? data.data : null;
        if (!announcement || announcement.isRead) return;
        this.latestAnnouncement = announcement;
        this.announcementDialogVisible = true;
        const readResponse = await markAnnouncementRead(announcement.id);
        if (readResponse.data && readResponse.data.code === 0) announcement.isRead = true;
      } catch (error) {
        // Announcement reminders are non-critical.
      }
    },
    openFeedback() {
      this.$router.push({ name: 'dashboardFeedback' });
    },
    recordTutorialAccess() {
      // The tutorial opens an external page in a new tab, so it does not pass
      // through the SPA route tracker. Record the click explicitly instead.
      recordAccess(TUTORIAL_URL, window.location.href).catch(() => {});
    },
    handleFeedbackReadCompleted() {
      this.loadUnreadFeedbackCount();
    },
    async loadCheckinAvailability() {
      if (this.checkinAvailabilityLoading) {
        this.checkinAvailabilityReloadRequested = true;
        return false;
      }
      this.checkinAvailabilityLoading = true;
      try {
        const { data } = await fetchCheckinAvailability();
        const availability = data && data.code === 0 && data.data;
        if (!availability) {
          this.checkinAllowed = false;
          this.checkinVisible = false;
          this.checkinCheckedToday = false;
          return false;
        }
        this.applyCheckinAvailability(availability);
        this.$root.$emit('checkin-availability-updated', availability);
        return true;
      } catch (error) {
        this.checkinAllowed = false;
        this.checkinVisible = false;
        this.checkinCheckedToday = false;
        return false;
      } finally {
        this.checkinAvailabilityLoading = false;
        this.checkinAvailabilityLoaded = true;
        if (this.checkinAvailabilityReloadRequested) {
          this.checkinAvailabilityReloadRequested = false;
          this.loadCheckinAvailability();
        }
      }
    },
    applyCheckinAvailability(availability) {
      this.checkinVisible = typeof availability.visible === 'boolean' ? availability.visible : availability.allowed === true;
      this.checkinAllowed = this.checkinVisible && availability.allowed === true;
      this.checkinCheckedToday = Boolean(availability.checkedToday);
      this.checkinQuotaRemaining = availability.quota ? Number(availability.quota.remainingAmount) : null;
      this.checkinAvailabilityLoaded = true;
    },
    async openCheckinPage() {
      if (this.checkinAvailabilityLoading) return;
      recordAccess(CHECKIN_ACCESS_URL, window.location.href).catch(() => {});
      const loaded = await this.loadCheckinAvailability();
      if (!loaded) {
        this.$message.error('领取状态加载失败，请稍后重试');
        return;
      }
      if (!this.checkinVisible) return;
      if (this.$route.name !== 'dashboardCheckin') this.$router.push({ name: 'dashboardCheckin' });
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    async loadCustomerService() {
      try {
        const { data } = await fetchProfile();
        if (data && data.code === 0 && data.data) {
          this.customerService = String(data.data.customerService || '').trim();
          this.timeoutCompensationEnabled = Number(data.data.allowTimeoutCompensation) === 1;
          this.proxyEnabled = Number(data.data.allowProxy) === 1;
        }
      } catch (error) {
        this.customerService = '';
        this.timeoutCompensationEnabled = false;
        this.proxyEnabled = false;
      }
    },
    handleUserCommand(command) {
      if (command === 'logout') {
        window.localStorage.removeItem(LOGIN_STORAGE_KEY);
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        this.$message.success('已退出登录');
        window.location.href = '/login';
      }
    }
  }
};
</script>
